import React, {useRef, useState, forwardRef, useImperativeHandle} from 'react';
import {SoopInputBox, SoopButtonCommon} from '@soop-ui/soop-ui';
import styles from './FileInputUI.module.scss';
import {UseFormTrigger, FieldValues, FieldError} from 'react-hook-form';
import {useHelpContext} from '@/app/(main)/routes/help/context/HelpContext';
import FormError from '@/components/help/common/FormContainer/FormError';
import {FORM_FIELDS} from '@/constants/help/form';
import { useFormKeyHandler } from '@/hooks/help/useFormKeyHandler';
export interface FileInputUIRef {
  trigger: () => void;
  clear: () => void;
}

interface FileInputUIProps {
  value?: File[];
  onChange: (files: File[]) => void;
  placeholder?: string;
  triggerValidation?: UseFormTrigger<FieldValues>;
  error?: FieldError;
}

const FileInputUI = forwardRef<FileInputUIRef, FileInputUIProps>(
  (
    {
      value = [],
      onChange,
      placeholder = '첨부 파일',
      triggerValidation,
      error,
    }: FileInputUIProps,
    ref,
  ) => {
    const {isMobile} = useHelpContext();
    const {handleFormKeyDown} = useFormKeyHandler();
    // 실제 파일 선택을 담당할 숨겨진 input을 위한 ref
    const realInputRef = useRef<HTMLInputElement>(null);
    const attachedFiles = value || [];
    const [displayText, setDisplayText] = useState('');

    useImperativeHandle(ref, () => ({
      // 파일 선택창을 여는 함수
      trigger: () => {
        realInputRef.current?.click();
      },
      // 첨부된 파일을 모두 초기화하는 함수
      clear: () => {
        onChange([]);
        setDisplayText('');
      },
    }));

    // "찾아보기" 버튼 클릭 시 숨겨진 input을 클릭
    const handleTriggerClick = (e) => {
      e.preventDefault();
      realInputRef.current?.click();
    };

    // 파일이 실제로 변경되었을 때의 이벤트 핸들러
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || e.target.files.length === 0) {
        return;
      }
      const newFiles = Array.from(e.target.files);
      const updatedFiles = [...attachedFiles, ...newFiles];

      onChange(updatedFiles);

      const lastAddedFile = newFiles[newFiles.length - 1];
      if (lastAddedFile) {
        setDisplayText(lastAddedFile.name);
      }

      if (triggerValidation) {
        triggerValidation(FORM_FIELDS.FILES);
      }

      e.target.value = '';
    };

    // 파일 제거 핸들러
    const handleRemoveFile = (indexToRemove: number) => {
      const updatedFiles = attachedFiles.filter(
        (_, index) => index !== indexToRemove,
      );

      onChange(updatedFiles);

      if (updatedFiles.length > 0) {
        const lastFileInList = updatedFiles[updatedFiles.length - 1];
        setDisplayText(lastFileInList.name);
      } else {
        setDisplayText('');
      }

      if (triggerValidation) {
        triggerValidation(FORM_FIELDS.FILES);
      }
    };

    return (
      <div>
        {/* 사용자가 보게 될 UI 부분 */}
        <div className={styles.fileInputContainer}>
          {/* 보여주기 위한 가짜 입력창 */}
          <SoopInputBox
            type='text'
            className={styles.fileInputBox}
            readOnly
            value={displayText}
            size={44}
            placeholder={placeholder}
            onClick={handleTriggerClick}
            onKeyDown={handleFormKeyDown}
            clearable={false}
          />
          {/* 파일 찾기 트리거 버튼 */}
          <SoopButtonCommon
            {...(isMobile && {endIconName: 'icoPlus'})}
            type='button'
            className={styles.fileUploadButton}
            size={!isMobile ? 44 : 40}
            variant='defaultSecondary'
            onClick={handleTriggerClick}
            tabIndex={0}
            >
            {!isMobile ? '찾아보기' : '파일 첨부'}
          </SoopButtonCommon>
        </div>
        <FormError error={error} className={styles.fileInputError} />

        {/* 실제 파일 선택 로직을 처리하는 숨겨진 진짜 입력창 */}
        <input
          type='file'
          ref={realInputRef}
          multiple
          onChange={handleFileChange}
          className={styles.hiddenInput}
        />
        <p className={styles.fileInputInfo}>
          첨부 가능 확장자: gif, jpg, jpeg, png, txt, zip, bmp
          <br />
          10M 이상 첨부파일은 helpdesk@sooplive.co.kr로 첨부 해주시기 바랍니다.
        </p>

        {/* 첨부된 파일 목록 */}
        {attachedFiles.length > 0 && (
          <div className={styles.fileList}>
            {attachedFiles.map((file, index) => (
              <div key={index} className={styles.fileItem}>
                <span>{file.name}</span>
                <SoopButtonCommon
                  type='button'
                  height={16}
                  iconName='icoClose'
                  onClick={() => handleRemoveFile(index)}
                  variant='clearSecondary'
                  width={16}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  },
);

FileInputUI.displayName = 'FileInputUI';

export default FileInputUI;
